import { createColumnHelper } from '@tanstack/react-table';
import { isAfter, isBefore } from 'date-fns';
import { useMemo, useState } from 'react';
import { LuDownload, LuPlus } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { FeeDetails, FeeSource, FeeType, GlobalAccount, GUID } from '@ndvr/billing-api';

import { Button } from '@/components/ui/button';
import { formatCurrency, formatDate, titleCase, useRequiredParams } from 'utils';
import { useFeeDetails } from 'views/billing/billing-queries';
import { PortfolioSelect } from 'views/billing/widgets/PortfolioSelect';
import { HouseholdHeaderActionsPortal } from 'views/household/HouseholdIndividualPage';
import {
    AccountNameLink,
    CustomTablePage,
    FormLabel,
    FormNumberInput,
    FormSelect,
    FormYesNoToggle,
    TableFilters,
    Text,
    useTableFilter,
    VerticalLayout,
} from 'widgets';
import { FormDateInput } from 'widgets/form/input/FormDateInput';
import { exportTableToCsv } from 'widgets/table/exportTableToCsv';

import { NewFeeDrawer } from './NewFeeDrawer';

const columnHelper = createColumnHelper<FeeDetails>();

export const BillingHistoryTab = () => {
    const navigate = useNavigate();

    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { data: feeDetails, isLoading } = useFeeDetails(globalAccountGUID);

    const [newFeeDrawerOpen, setNewFeeDrawerOpen] = useState(false);

    const filters = useMemo<TableFilters<FeeDetails>>(getFeeFilters, []);
    const { FilterButton, FilterBar, filteredData } = useTableFilter(feeDetails, filters, {}, 'billingHistoryFilter');

    const columns = [
        columnHelper.accessor((fee) => fee.feeEntry.settlementPortfolioGUID, {
            id: 'settlementPortfolioGUID',
            header: 'Settlement Account',
            cell: ({ getValue }) => {
                const settlementPortfolioGUID = getValue();
                return settlementPortfolioGUID ? <AccountNameLink accountGUID={settlementPortfolioGUID} /> : '-';
            },
            minSize: 200,
        }),
        columnHelper.accessor((fee) => fee.feeEntry.generatingPortfolioGUID, {
            id: 'generatingPortfolioGUID',
            header: 'Generating Account',
            cell: ({ getValue }) => {
                const generatingPortfolioGUID = getValue();
                return generatingPortfolioGUID ? <AccountNameLink accountGUID={generatingPortfolioGUID} /> : '-';
            },
            minSize: 200,
        }),
        columnHelper.accessor((fee) => fee.feeEntry.feeType, {
            id: 'feeType',
            header: 'Fee Type',
            cell: ({ getValue, row }) => {
                const feeType = titleCase(getValue());
                return row.original.feeSource === FeeSource.MANUAL_FEE ? feeType + ' (Manual)' : feeType;
            },
            minSize: 120,
        }),
        columnHelper.accessor('totalFeeAmount', {
            header: 'Amount',
            cell: ({ getValue, row }) => formatCurrency(getValue(), row.original.feeEntry.currency, 2),
            sortingFn: 'alphanumeric',
            minSize: 100,
        }),
        columnHelper.accessor('totalSettledFeeAmount', {
            header: 'Settled',
            cell: ({ getValue, row }) => formatCurrency(getValue(), row.original.feeEntry.currency, 2),
            sortingFn: 'alphanumeric',
            minSize: 100,
        }),
        columnHelper.accessor('amendmentCount', {
            header: 'Amendments',
            cell: ({ getValue }) => getValue() || '-',
            minSize: 100,
        }),
        columnHelper.accessor((fee) => fee.feeEntry.billingDate, {
            id: 'billingDate',
            header: 'Billing Date',
            cell: ({ getValue }) => formatDate(getValue()),
            minSize: 100,
        }),
    ];

    return (
        <>
            <HouseholdHeaderActionsPortal>
                <div className="flex items-center gap-4">
                    <FilterButton>
                        <FeeFilterForm globalAccountGUID={globalAccountGUID} />
                    </FilterButton>
                    <Button variant="outline2" size="sm" onClick={() => setNewFeeDrawerOpen(true)}>
                        <LuPlus className="h-4 w-4" />
                        <span>Manual Fee</span>
                    </Button>
                </div>
            </HouseholdHeaderActionsPortal>

            <VerticalLayout $gap={16}>
                {FilterBar}

                {filteredData.length > 0 ? (
                    <CustomTablePage
                        rowOnClick={(fee) => navigate(`fee/${fee.feeEntry.feeEntryGUID}`)}
                        data={filteredData}
                        isLoading={isLoading}
                        columns={columns}
                        defaultSortCol="billingDate"
                        defaultSortDirection="desc"
                        exportName="billingHistory"
                        toolbarPosition="header"
                        renderToolbar={(table) => (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => exportTableToCsv({ table, exportName: 'billingHistory' })}
                            >
                                <LuDownload className="h-4 w-4" />
                                Export CSV
                            </Button>
                        )}
                    />
                ) : (
                    <Text $italic $themeColor="subtle">
                        No fees
                    </Text>
                )}
            </VerticalLayout>

            {newFeeDrawerOpen && <NewFeeDrawer onClose={() => setNewFeeDrawerOpen(false)} />}
        </>
    );
};

enum SettlementStatus {
    SETTLED = 'Settled',
    UNSETTLED = 'Unsettled',
}

enum AmendmentStatus {
    AMENDED = 'Amended',
    NOT_AMENDED = 'Not Amended',
}

const FeeFilterForm = ({ globalAccountGUID }: { globalAccountGUID: GUID<GlobalAccount> }) => {
    return (
        <VerticalLayout $gap={24}>
            <VerticalLayout $gap={4}>
                <FormLabel>Settlement account</FormLabel>
                <PortfolioSelect
                    allowEmpty
                    hideLabel
                    name="settlementPortfolioGUID"
                    globalAccountGUID={globalAccountGUID}
                />
                <FormLabel>Generating account</FormLabel>
                <PortfolioSelect
                    allowEmpty
                    hideLabel
                    name="generatingPortfolioGUID"
                    globalAccountGUID={globalAccountGUID}
                />
            </VerticalLayout>

            <FormSelect vertical name="feeType" label="Fee type">
                <option></option>
                <option value={FeeType.AUM}>AUM</option>
                <option value={FeeType.MANAGEMENT}>Management</option>
            </FormSelect>

            <VerticalLayout $gap={4}>
                <FormNumberInput
                    name="minFeeAmount"
                    label="Min fee amount"
                    placeholder="enter min amount"
                    allowNegative={false}
                />
                <FormNumberInput
                    name="maxFeeAmount"
                    label="Max fee amount"
                    placeholder="enter max amount"
                    allowNegative={false}
                />
            </VerticalLayout>

            <VerticalLayout $gap={8}>
                <FormYesNoToggle
                    name="settlementStatus"
                    label="Settlement status"
                    yesLabel={SettlementStatus.SETTLED}
                    noLabel={SettlementStatus.UNSETTLED}
                    yesValue={SettlementStatus.SETTLED}
                    noValue={SettlementStatus.UNSETTLED}
                />
                <FormYesNoToggle
                    name="amendmentStatus"
                    label="Amendment status"
                    yesLabel={AmendmentStatus.AMENDED}
                    noLabel={AmendmentStatus.NOT_AMENDED}
                    yesValue={AmendmentStatus.AMENDED}
                    noValue={AmendmentStatus.NOT_AMENDED}
                />
            </VerticalLayout>

            <VerticalLayout $gap={4}>
                <FormDateInput name="billingDateFrom" label="Billing date from" preventFlip />
                <FormDateInput name="billingDateTo" label="Billing date to" preventFlip />
            </VerticalLayout>
        </VerticalLayout>
    );
};

const getFeeFilters = (): TableFilters<FeeDetails> => ({
    settlementPortfolioGUID: {
        label: 'Settlement account',
        filterFunction: (fee, filterValue) => fee.feeEntry.settlementPortfolioGUID === filterValue,
        valueDecoder: (filterValue) => filterValue,
    },
    generatingPortfolioGUID: {
        label: 'Generating account',
        filterFunction: (fee, filterValue) => fee.feeEntry.generatingPortfolioGUID === filterValue,
        valueDecoder: (filterValue) => filterValue,
    },
    feeType: {
        label: 'Fee type',
        filterFunction: (fee, filterValue) => fee.feeEntry.feeType === filterValue,
        valueDecoder: (filterValue) => titleCase(filterValue),
    },
    minFeeAmount: {
        label: 'Min fee amount',
        filterFunction: (fee, filterValue) => Number(fee.totalFeeAmount) >= Number(filterValue),
        valueDecoder: (filterValue) => filterValue,
    },
    maxFeeAmount: {
        label: 'Max fee amount',
        filterFunction: (fee, filterValue) => Number(fee.totalFeeAmount) <= Number(filterValue),
        valueDecoder: (filterValue) => filterValue,
    },
    settlementStatus: {
        label: 'Settlement status',
        filterFunction: (fee, filterValue) => {
            const settlementStatus =
                fee.totalFeeAmount === fee.totalSettledFeeAmount
                    ? SettlementStatus.SETTLED
                    : SettlementStatus.UNSETTLED;
            return settlementStatus === filterValue;
        },
        valueDecoder: (filterValue) => filterValue,
    },
    amendmentStatus: {
        label: 'Amendment status',
        filterFunction: (fee, filterValue) => {
            const amendmentStatus =
                fee.feeSource === FeeSource.MANUAL_FEE || fee.amendmentCount > 0
                    ? AmendmentStatus.AMENDED
                    : AmendmentStatus.NOT_AMENDED;
            return amendmentStatus === filterValue;
        },
        valueDecoder: (filterValue) => filterValue,
    },
    billingDateFrom: {
        label: 'Billing date from',
        filterFunction: (fee, filterValue) => {
            const billingDate = new Date(fee.feeEntry.billingDate);
            const billingDateFrom = new Date(filterValue);
            return !isBefore(billingDate, billingDateFrom);
        },
        valueDecoder: (filterValue) => formatDate(filterValue),
    },
    billingDateTo: {
        label: 'Billing date to',
        filterFunction: (fee, filterValue) => {
            const billingDate = new Date(fee.feeEntry.billingDate);
            const billingDateTo = new Date(filterValue);
            return !isAfter(billingDate, billingDateTo);
        },
        valueDecoder: (filterValue) => formatDate(filterValue),
    },
});
